import { IEntityInput, IHomeItemInput } from "@types";
import Link from "next/link";
import useParentSize from "@hooks/useParentSize";
import ImagePrepare from "@elements/imagePrepare";
import { linkGenerator } from "@utils/linkGenerator";
import useBannerViewed from "@hooks/useBannerViewed";
import { actionGenerator } from "@utils/actionGenerator";
import useBrandView from "@hooks/useBrandViewed";
import useCollectionViewed from "@hooks/useCollectionViewed";
import useAllCategoriesViewed from "@hooks/useAllCategoriesViewed";
import useCategoryViewed from "@hooks/useCategoryViewed";

interface MainBannerProps {
    data: IHomeItemInput;
    sourceEntity?: IEntityInput;
}

const MainBanner: React.FC<MainBannerProps> = ({ data, sourceEntity }) => {
    const [size, parentRef] = useParentSize();
    const { bannerViewed } = useBannerViewed();
    const { brandViewed } = useBrandView();
    const { collectionViewed } = useCollectionViewed();
    const { allCategoriesViewed } = useAllCategoriesViewed();
    const { categoryViewed } = useCategoryViewed();

    // Since mainBanner always has one item, we can directly access it
    const item = data.items[0];
    if (!item) return null;

    let target = "_self";
    let rel = "";

    if (item.action.includes("openURL") && !item.action.includes("openURLinApp")) {
        target = "_blank";
        rel = "noopener noreferrer";
    }

    const source = {
        from_page: "homepage",
        from_entity: "home",
        from_entity_slug: sourceEntity?.slug || "",
        from_page_section: "",
        from_page_section_entity: "mainBanner",
        from_page_section_slug: item.slug || "",
    };

    return (
        <div className="w-full max-w-[1240px] m-auto my-4" ref={parentRef}>
            <div className="mb-4">
                <div className="relative rounded-2xl overflow-hidden cursor-pointer">
                    <Link
                        href={linkGenerator(item.action, {
                            source: "homepage",
                            sourceEntity: "home",
                            sourceSlug: sourceEntity?.slug,
                            section: "",
                            sectionEntity: "mainBanner",
                            sectionSlug: item.slug || "",
                            target_slug: item.target?.slug || ""
                        })}
                        passHref
                        prefetch={false}
                        target={target}
                        rel={rel}
                        onClick={() => {
                            bannerViewed(
                                source,
                                { id: item.id, slug: item.slug || "" },
                                { id: item.target?.id, slug: item.target?.slug || "" },
                                item.action
                            );

                            if (actionGenerator(item.action) === "Category") {
                                categoryViewed(source, { id: item.target?.id, slug: item.target?.slug });
                            }

                            if (actionGenerator(item.action) === "Categories List") {
                                allCategoriesViewed(source);
                            }

                            if (actionGenerator(item.action) === "Collection") {
                                collectionViewed(source, { id: item.target?.id, slug: item.target?.slug });
                            }

                            if (actionGenerator(item.action) === "Brand") {
                                brandViewed(source, { id: item.target?.id, slug: item.target?.slug });
                            }
                        }}
                        legacyBehavior
                    >
                        <div className="w-full pt-[30.32%] relative">
                            <div className="w-full rounded-2xl overflow-hidden h-full absolute inset-0">
                                <ImagePrepare media={item.media} alt={item.name} width={size.width} />
                            </div>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default MainBanner; 